import useMyProfileStore from "@/hooks/useMyProfileStore";
import ProfileEndorsementSection from "./ProfileEndorsementSection";
import { useMemo } from "react";
import { Button, Card, Modal } from "antd";
import { HiFlag } from "react-icons/hi";
import Link from "next/link";
import CardUserProfile from "@/components/Card/card-user-profile";
import { userTypeConvert } from "@/utils/helper";
import { ReadMoreSpan } from "@/components/ModalReadMore";
import useVisitOtherProfileStore from "@/hooks/useVisitOtherProfileStore";
import SwiperMC from "@/components/SwiperMC";
import { useRouter } from "next/router";
import { DiamondWhite } from "@/assets/icons";
import { useIsViewAsHR } from "@/hooks/useIsViewAsHR";

const ProfileEndorsementWidget = ({ isVisiting, openEndorsementModal }: { isVisiting?: boolean, openEndorsementModal: () => void }) => {
  const router = useRouter();
  const { userView, setState } = useMyProfileStore();
  const { user } = useVisitOtherProfileStore();
  const { isViewAsHR } = useIsViewAsHR();

  const dataEndorsement = useMemo(() => {
    const sourceData = isVisiting ? user : userView;
    return (
      sourceData?.endorsements?.filter(
        (item: any) => item?.status === "APPROVED",
      ) || []
    );
  }, [userView?.endorsements, user?.endorsements, isVisiting]);

  return (
    <div className="flex flex-col gap-4">
      <ProfileEndorsementSection showViewMore={dataEndorsement?.length > 7} />

      {/* No endorsement message */}
      {dataEndorsement?.length === 0 && (
        <>
          {router.pathname === "/profile" && <Card>Your connections earn points by endorsing you.</Card>}
          {router.pathname !== "/profile" && (
            user?.userConnectionType && (!user?.pendingConnection) ? (
              <Card>
                Be the first to write an endorsement about me and earn more points!
                {
                  !isViewAsHR && (
                    <Button
                      onClick={openEndorsementModal}
                      type="primary"
                      className="w-full mt-4 sm:mt-2 md:mt-4"
                    >
                      <div className="flex items-center gap-2 text-center justify-center text-md text-semibold">
                        Write Endorsement <DiamondWhite />
                      </div>
                    </Button>
                  )
                }
              </Card>
            ) : (
              <Card>Connect with me to write an endorsement and earn points!</Card>
            )
          )}
        </>
      )}

      {/* Endorsements > 0 */}
      {dataEndorsement?.length > 0 ? (
        <SwiperMC
          data={dataEndorsement || []}
          hideNavigation={dataEndorsement?.length < 2}
          classNameSlide="!h-auto"
          render={(item, idx) => (
            <div key={`card-Endorsements-${idx}`} className="h-full">
              <Card
                className="h-full flex flex-col"
                bodyStyle={{
                  flex: 1,
                }}
                actions={
                  isVisiting
                    ? [
                      <Button
                        type="text"
                        icon={<HiFlag />}
                        onClick={() =>
                          setState({
                            redflagModalConfig: {
                              referenceType: "ENDORSEMENT",
                              isOpen: true,
                              referenceID: item?.id,
                            },
                          })
                        }
                      >
                        {/* Report */}
                      </Button>,
                    ]
                    : []
                }
              >
                <Link
                  href={`/profile/${item?.createdBy?.id}`}
                  className="flex gap-4 items-center mb-6 last:mb-0"
                >
                  <div className="inline-block">
                    <CardUserProfile
                      avatarOnly
                      avatarSize="sm"
                      type="profile"
                      disableAvatarOnlyHref={true}
                      item={item?.createdBy}
                      points={item?.createdBy?.points}
                      findCG
                      reference={item?.createdBy?.id}
                    />
                  </div>

                  <div className="flex-1">
                    <p className="text-neutral-500">
                      {item?.createdBy?.jobTitle
                        ? item?.createdBy?.jobTitle
                        : "----"}
                    </p>
                    <h4 className="text-lg font-bold text-white">
                      {`${item?.createdBy?.firstName} ${item?.createdBy?.lastName}`}
                    </h4>
                    <p className="text-neutral-500">
                      {item?.createdBy?.type
                        ? `${userTypeConvert(item?.createdBy?.type)},`
                        : ""}{" "}
                      Actively Looking for{" "}
                      {userTypeConvert(item?.createdBy?.lookingForType)}
                    </p>
                  </div>
                </Link>

                <div className="text-base">
                  <ReadMoreSpan
                    title={`${item?.createdBy?.firstName} ${item?.createdBy?.lastName}`}
                    content={`${item?.description}`}
                    maxTruncate={110}
                    usePathname={true}
                  />
                </div>
              </Card>
            </div>
          )}
        />
      ) : null}
      {user?.userConnectionType && !user?.pendingConnection && dataEndorsement.length > 0 && router.pathname !== "/profile" &&
        !dataEndorsement.some(
          (e: any) => e.endorsementCreatedById === userView?.userId,
        ) && (
          !isViewAsHR && (
            <Button
              onClick={openEndorsementModal}
              type="primary"
              className="w-full mt-4 sm:mt-2 md:mt-4"
            >
              <div className="flex items-center gap-2 text-center justify-center text-md text-semibold">
                Write Endorsement <DiamondWhite />
              </div>
            </Button>
          )
        )}

      <Modal
        title="Endorsements"
        open={router.query?.myendorsements === "true"}
        onCancel={() => {
          const prevQ = router.query;
          delete prevQ.myendorsements;
          router.push({ pathname: router.pathname, query: prevQ }, undefined, {
            shallow: true,
          });
        }}
        footer={null}
      >
        <div className="flex flex-col gap-8 pt-8">
          {dataEndorsement?.map((item, i) => (
            <div className="flex gap-4" key={i}>
              <CardUserProfile
                item={{
                  id: item?.createdBy?.id as never,
                  ...(item?.createdBy as any),
                }}
                findCG
                points={item?.createdBy?.points as number}
                avatarOnly
                avatarSize="sm"
              />

              <div className="flex flex-col gap-2">
                <h3 className="font-bold text-lg capitalize">
                  {item?.createdBy?.firstName} {item?.createdBy?.lastName}
                </h3>
                <p className="text-sm">{item?.description}</p>
              </div>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export default ProfileEndorsementWidget;